import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './DeviceList.css';

export const deviceHasMaintenanceSchedule = (device) => {
  if (!device) return false;
  return Boolean(
    device.ppm_next_due_date
    || (device.calibration_required && device.calibration_next_due_date)
    || (device.compliance_required && device.compliance_next_due_date)
  );
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

const getDaysLeft = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(dateString);
  dueDate.setHours(0, 0, 0, 0);
  return Math.ceil((dueDate - today) / (1000 * 3600 * 24));
};

const getDaysLeftColor = (daysLeft) => {
  if (daysLeft === null) return '#6c757d';
  if (daysLeft < 0) return '#dc3545';
  if (daysLeft === 0) return '#ffc107';
  if (daysLeft <= 7) return '#fd7e14';
  if (daysLeft <= 30) return '#ffc107';
  return '#28a745';
};

const getDaysLeftLabel = (daysLeft) => {
  if (daysLeft === null) return 'N/A';
  if (daysLeft < 0) return `Overdue by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''}`;
  if (daysLeft === 0) return 'Due today';
  if (daysLeft === 1) return '1 day left';
  return `${daysLeft} days left`;
};

const LastDoneCell = ({ date, hasLog }) => {
  if (!date) return <span className="na-text">Never</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div>{formatDate(date)}</div>
      {!hasLog && (
        <span style={{
          fontSize: '0.7rem',
          color: '#856404',
          backgroundColor: 'white',
          padding: '2px 4px',
          borderRadius: '3px',
          display: 'inline-block',
          width: 'fit-content',
          fontWeight: '600',
        }}
        >
          ℹ️
        </span>
      )}
    </div>
  );
};

const NextDueCell = ({ date }) => {
  const daysLeft = getDaysLeft(date);
  return (
    <>
      <div>{formatDate(date)}</div>
      <span
        className="days-left-badge"
        style={{
          backgroundColor: getDaysLeftColor(daysLeft),
          color: '#fff',
          padding: '3px 6px',
          borderRadius: '3px',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'inline-block',
          marginTop: '4px',
        }}
      >
        {getDaysLeftLabel(daysLeft)}
      </span>
    </>
  );
};

/**
 * Maintenance Schedule modal from the Devices list (calendar icon).
 * Shows PPM / Calibration / Compliance last done, next due, and frequency.
 */
const DeviceMaintenanceScheduleModal = ({ device, token, onClose }) => {
  const [hasLogsForType, setHasLogsForType] = useState({});
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!device?.id || !token) {
      setHasLogsForType({});
      return undefined;
    }

    let cancelled = false;
    setHasLogsForType({});

    axios.get(API_ENDPOINTS.DEVICES.MAINTENANCE(device.id), {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      if (cancelled) return;
      const lastDates = {};
      const logs = {};
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        response.data.forEach((log) => {
          const typeKey = log.maintenance_type ? log.maintenance_type.toLowerCase().trim() : '';
          let normalizedType = typeKey;
          if (typeKey.includes('ppm') || typeKey === 'ppm') normalizedType = 'ppm';
          else if (typeKey.includes('calibration')) normalizedType = 'calibration';
          else if (typeKey.includes('compliance')) normalizedType = 'compliance';

          if (!lastDates[normalizedType] || new Date(log.maintenance_date) > new Date(lastDates[normalizedType])) {
            lastDates[normalizedType] = log.maintenance_date;
            logs[normalizedType] = true;
          }
        });
      }
      setHasLogsForType(logs);
    }).catch((error) => {
      console.error('Error fetching maintenance history:', error);
      if (!cancelled) setHasLogsForType({});
    });

    return () => {
      cancelled = true;
    };
  }, [device?.id, token]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (!device) return null;

  const showPpm = Boolean(device.ppm_next_due_date);
  const showCalibration = Boolean(device.calibration_required && device.calibration_next_due_date);
  const showCompliance = Boolean(device.compliance_required && device.compliance_next_due_date);

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="next-due-modal">
        <div className="next-due-modal-header">
          <div className="next-due-header-content">
            <h3>{device.name}</h3>
            <DeviceIDBadge
              deviceId={device.device_identifier || 'N/A'}
              size="small"
            />
          </div>
          <button type="button" className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <table className="maintenance-schedule-table">
            <thead>
              <tr>
                <th>Maintenance Type</th>
                <th>Last Done</th>
                <th>Next Due Date</th>
                <th>Frequency</th>
              </tr>
            </thead>
            <tbody>
              {showPpm && (
                <tr>
                  <td><span className="maintenance-type-icon">🔧</span> Preventive Maintenance (PPM)</td>
                  <td>
                    <LastDoneCell
                      date={device.ppm_last_done_date}
                      hasLog={Boolean(hasLogsForType.ppm)}
                    />
                  </td>
                  <td><NextDueCell date={device.ppm_next_due_date} /></td>
                  <td>{device.ppm_frequency || 'N/A'}</td>
                </tr>
              )}
              {showCalibration && (
                <tr>
                  <td><span className="maintenance-type-icon">⚖️</span> Calibration</td>
                  <td>
                    <LastDoneCell
                      date={device.calibration_last_done_date}
                      hasLog={Boolean(hasLogsForType.calibration)}
                    />
                  </td>
                  <td><NextDueCell date={device.calibration_next_due_date} /></td>
                  <td>{device.calibration_frequency || 'N/A'}</td>
                </tr>
              )}
              {showCompliance && (
                <tr>
                  <td><span className="maintenance-type-icon">✅</span> Regulatory Compliance</td>
                  <td>
                    <LastDoneCell
                      date={device.compliance_last_done_date}
                      hasLog={Boolean(hasLogsForType.compliance)}
                    />
                  </td>
                  <td><NextDueCell date={device.compliance_next_due_date} /></td>
                  <td>{device.compliance_frequency || 'N/A'}</td>
                </tr>
              )}
              {!showPpm && !showCalibration && !showCompliance && (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', color: '#6c757d', fontStyle: 'italic' }}>
                    No maintenance schedule configured for this device.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: '#fffbf0',
            borderLeft: '4px solid #ff9800',
            borderRadius: '3px',
            flexShrink: 0,
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#666' }}>
              <span style={{ fontSize: '0.7rem', color: '#856404', backgroundColor: '#fffbf0', padding: '2px 4px', borderRadius: '2px', fontWeight: '600' }}>ℹ️</span>
              <span>Data captured when device was added to the system - no maintenance log created yet.</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeviceMaintenanceScheduleModal;
